import DownloadIcon from "@/assets/campaign/FFFFFF_purchase_icon.svg?react";
import CommentIcon from "@/assets/colorable/comment.svg?react";
import HeartIcon from "@/assets/colorable/heart.svg?react";
import RightArrowIcon from "@/assets/colorable/right_arrow.svg?react";
import ViewsIcon from "@/assets/colorable/views.svg?react";
import Button from "@/components/Common/Button";
import useContentIcons from "@/hooks/useContentIcons";
import {useSocialNumberFormat} from "@/hooks/useSocialNumberFormat";
import {useIsAdmin} from "@/hooks/useUserHooks";
import ContentPurchaseButton from "@/pages/plans/ContentPurchaseButton";
import {Can} from "@/permissions";
import {useCarouselStore, type TCarouselMedia} from "@/store/carousel";
import dayjs from "dayjs";
import {memo, useMemo, type FC} from "react";
import {useTranslation} from "react-i18next";
import {useLocation} from "react-use";
import useDownloader from "react-use-downloader";

const CarouselFooter: FC<{content: TCarouselMedia}> = ({content}) => {
  const {t} = useTranslation();
  const {download} = useDownloader({cache: "no-cache"});
  const location = useLocation();
  const formatNumber = useSocialNumberFormat();
  const isAdmin = useIsAdmin();
  const isOnInfluencerDetailPage = useMemo(() => location.pathname?.includes("/influencer/"), [location.pathname]);
  const variant = useCarouselStore(state => state.variant);
  const contentIcons = useContentIcons("ALL");
  const ContentIcon = useMemo(() => contentIcons[content.instagram_media?.media_product_type!]?.Icon, [contentIcons]);
  const shouldShowDownloadOption = useMemo(
    () =>
      isAdmin ||
      (content.instagram_media?.media_product_type !== "SOURCE" &&
        (content.instagram_media?.media_product_type === "UGC"
          ? content.ugcInformation?.revisionStatus === "approved"
          : true) &&
        !isOnInfluencerDetailPage &&
        variant !== "social_feed"),
    [
      content.instagram_media?.media_product_type,
      content.ugcInformation?.revisionStatus,
      isAdmin,
      isOnInfluencerDetailPage,
      variant,
    ],
  );

  if (variant === "social_feed") {
    return (
      <div className="mt-7 flex flex-wrap items-center justify-center gap-3 sm:flex-nowrap">
        <div className="border-neutral-white-dark flex min-h-12 flex-col flex-wrap items-center justify-center gap-2 rounded-md border-1 bg-black/80 px-3.5 py-3 text-sm text-white sm:flex-row sm:gap-3 sm:px-5 md:w-full md:max-w-[450px]">
          <div className="flex items-center gap-2">
            {typeof content.instagram_media?.play_count === "number" && (
              <div className="flex items-center gap-1">
                <ViewsIcon className="h-4 w-auto shrink-0 text-white sm:h-4.5" />
                <span className="text-xs font-semibold text-white sm:text-sm">
                  {formatNumber(content.instagram_media.play_count)}
                </span>
              </div>
            )}
            {typeof content.instagram_media?.like_count === "number" && (
              <div className="flex items-center gap-1">
                <HeartIcon className="h-3 w-auto shrink-0 text-white sm:h-3.5" />
                <span className="text-xs font-semibold text-white sm:text-sm">
                  {formatNumber(content.instagram_media.like_count)}
                </span>
              </div>
            )}
            {typeof content.instagram_media?.comments_count === "number" && (
              <div className="flex items-center gap-1">
                <CommentIcon className="h-2.5 w-auto shrink-0 text-white sm:h-3" />
                <span className="text-xs font-semibold text-white sm:text-sm">
                  {formatNumber(content.instagram_media.comments_count)}
                </span>
              </div>
            )}
          </div>
          <div className="flex items-center gap-1.5">
            {ContentIcon && <ContentIcon className="size-3 shrink-0 sm:size-4" />}
            <span className="hidden whitespace-nowrap sm:block">
              {t("uploadedTime", {
                uploadDate: dayjs(content.instagram_media!.timestamp).format("DD.MM.YYYY"),
              })}
            </span>
            <span className="text-xs whitespace-nowrap sm:hidden">
              {dayjs(content.instagram_media!.timestamp).format("DD.MM.YYYY")}
            </span>
          </div>
        </div>
        {!!content.instagram_media?.permalink && (
          <button
            onClick={() => window.open(content.instagram_media?.permalink!, "_blank")}
            className="flex items-center justify-center rounded-md border border-white bg-black/80 p-3 transition-colors duration-150 hover:bg-black/60 sm:p-4">
            <RightArrowIcon className="size-3.5 shrink-0 text-white" />
          </button>
        )}
      </div>
    );
  }

  if (!content.user) {
    return (
      <Can I="download" a="Content">
        <Button
          className="mt-7 gap-2"
          shouldScaleOnHover={false}
          buttonStyle="Blue"
          onClick={() => download(content.instagram_media?.media_file!, content.campaign?.title!)}>
          <DownloadIcon className="z-10" />
          <span className="z-10">{t("downloadContent")}</span>
        </Button>
      </Can>
    );
  }

  if (!shouldShowDownloadOption) {
    return null;
  }

  return (
    <div className="flex justify-center">
      <Can I="download" a="Content" passThrough>
        {allowed => (
          <div className="border-neutral-white-dark mt-7 flex flex-col items-center justify-between gap-3 rounded-md border-1 bg-black/80 px-5 py-3 text-sm text-white sm:flex-row md:w-full md:max-w-[450px]">
            {allowed && <ContentPurchaseButton contents={[content]} className="w-full" />}
            <div
              className={
                allowed
                  ? "flex w-1/2 shrink-0 items-center justify-center gap-1"
                  : "flex h-10 w-full shrink-0 items-center justify-center gap-1"
              }>
              {ContentIcon && <ContentIcon className="size-4 shrink-0" />}
              <span className="whitespace-nowrap">
                {t("uploadedTime", {
                  uploadDate: dayjs(content.instagram_media!.timestamp ?? content.instagram_media?.created).format(
                    "DD.MM.YYYY",
                  ),
                })}
              </span>
            </div>
          </div>
        )}
      </Can>
    </div>
  );
};

export default memo(CarouselFooter);
